import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AdminShareTrade } from "@/components/AdminShareTrade";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { Shield, Loader2, LogOut, Users, MessageSquare, FileText, Cpu, DollarSign, BookOpen, KeyRound, Lock, Gift, Share2, BarChart3 } from "lucide-react";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="card-surface rounded-xl p-5">
    <div className="flex items-center justify-between"><span className="font-data text-[10px] uppercase tracking-widest text-slate-500">{label}</span><Icon className="h-4 w-4 text-brand" /></div>
    <div className="font-data text-3xl font-bold mt-2 text-white">{value}</div>
  </div>
);

const AdminLogin = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await login(email, password); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#070A0E] text-white grid place-items-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="card-surface rounded-2xl p-8 w-full max-w-md">
        <div className="flex items-center gap-2 mb-6"><div className="h-10 w-10 rounded-md bg-brand/15 border border-brand/40 grid place-items-center"><Shield className="h-5 w-5 text-brand" /></div><div><div className="font-heading font-extrabold text-lg">NETROY ADMIN</div><div className="font-data text-[9px] uppercase tracking-widest text-slate-500">Control Center</div></div></div>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Email</Label>
            <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0C1017] border-white/10 h-11 text-white" required /></div>
          <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Password</Label>
            <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0C1017] border-white/10 h-11 text-white" required /></div>
          {err && <p data-testid="admin-login-error" className="text-sm text-loss bg-loss/10 border border-loss/20 rounded-md px-3 py-2">{err}</p>}
          <Button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}</Button>
        </div>
      </form>
    </div>
  );
};

const ChangePassword = ({ forced }) => {
  const { checkAuth } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      await checkAuth();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className={forced ? "min-h-screen bg-[#070A0E] text-white grid place-items-center px-4" : ""}>
      <form onSubmit={submit} data-testid="admin-change-password-form" className="card-surface rounded-2xl p-8 w-full max-w-md">
        <div className="flex items-center gap-2 mb-2"><KeyRound className="h-5 w-5 text-amber" /><h2 className="font-heading text-xl font-bold">{forced ? "Set a new password" : "Change password"}</h2></div>
        {forced && <p className="text-sm text-slate-400 mb-5">For security, please change the default admin password before continuing.</p>}
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Current password</Label>
            <Input data-testid="admin-current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="bg-[#0C1017] border-white/10 h-11 text-white" required /></div>
          <div className="space-y-1.5"><Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">New password (min 8 chars)</Label>
            <Input data-testid="admin-new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="bg-[#0C1017] border-white/10 h-11 text-white" required /></div>
          <Button data-testid="admin-change-password-submit" type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Update Password</Button>
        </div>
      </form>
    </div>
  );
};

const MemberRow = ({ u, onGranted }) => {
  const [plan, setPlan] = useState(u.plan || "free");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const isAdmin = u.role === "admin";

  const grant = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.user_id}/plan`, { plan, days: Number(days) || 30 });
      toast.success(`${u.name} is now on the ${plan} plan`);
      onGranted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update plan");
    } finally { setBusy(false); }
  };

  const badgeCls = isAdmin
    ? "text-amber border-amber/30 bg-amber/10"
    : (u.plan === "elite" || u.plan === "pro") ? "text-brand border-brand/30 bg-brand/10" : "text-slate-400 border-white/10";

  return (
    <div data-testid="admin-member-row" className="rounded-lg bg-[#0C1017] border border-white/8 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0"><div className="text-sm text-white truncate">{u.name}</div><div className="font-data text-[11px] text-slate-500 truncate">{u.email}</div></div>
        <span className={`shrink-0 font-data text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border ${badgeCls}`}>{isAdmin ? "admin" : u.plan || "free"}</span>
      </div>
      {!isAdmin && (
        <div className="flex items-center gap-2 mt-2.5">
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger data-testid="admin-grant-plan-select" className="bg-[#070A0E] border-white/10 text-white h-8 text-xs w-28"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C1017] border-white/10 text-white">
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="elite">Elite</SelectItem>
            </SelectContent>
          </Select>
          {plan !== "free" && (
            <div className="flex items-center gap-1">
              <Input data-testid="admin-grant-days-input" type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="bg-[#070A0E] border-white/10 h-8 w-16 text-white text-xs" />
              <span className="font-data text-[10px] text-slate-500">days</span>
            </div>
          )}
          <Button data-testid="admin-grant-plan-btn" onClick={grant} disabled={busy} className="ml-auto h-8 bg-brand hover:bg-brand-hover text-white text-xs gap-1.5 px-3">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5" />} Grant
          </Button>
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showChangePw, setShowChangePw] = useState(false);

  const isAdmin = user && user.role === "admin";

  const loadAll = useCallback(async () => {
    try {
      const [s, u, m] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/contact/messages"),
      ]);
      setStats(s.data); setUsers(u.data.items || []); setMessages(m.data.items || []);
    } catch (e) { console.error("Failed to load admin data:", e); }
  }, []);

  useEffect(() => { if (isAdmin && !user.must_change_password) loadAll(); }, [isAdmin, user, loadAll]);

  if (loading) return <div className="min-h-screen bg-[#070A0E] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (!user) return <AdminLogin />;
  if (!isAdmin) return (
    <div className="min-h-screen bg-[#070A0E] text-white grid place-items-center px-4 text-center">
      <div><Shield className="h-10 w-10 text-loss mx-auto mb-4" /><h1 className="font-heading text-2xl font-bold">Admin access only</h1>
      <p className="text-slate-400 mt-2">This account is not an administrator.</p>
      <Button onClick={() => navigate("/")} className="mt-6 bg-brand hover:bg-brand-hover text-white">Back home</Button></div>
    </div>
  );
  if (user.must_change_password) return <ChangePassword forced />;

  return (
    <div className="min-h-screen bg-[#070A0E] text-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070A0E]/90 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5"><div className="h-9 w-9 rounded-md bg-brand/15 border border-brand/40 grid place-items-center"><Shield className="h-5 w-5 text-brand" /></div><div className="leading-none"><div className="font-heading font-extrabold tracking-tight text-[15px]">NETROY ADMIN</div><div className="font-data text-[9px] uppercase tracking-[0.25em] text-slate-500">Control Center</div></div></div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button data-testid="admin-open-change-pw" onClick={() => setShowChangePw((s) => !s)} variant="outline" className="bg-transparent border-white/15 text-white hover:bg-white/5 gap-2 h-9"><KeyRound className="h-4 w-4" /> Password</Button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-btn" className="h-9 w-9 grid place-items-center rounded-md border border-white/10 text-slate-400 hover:text-loss hover:border-loss/40"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div><h1 className="font-heading text-3xl font-black tracking-tight">Welcome, {user.name}</h1><p className="text-slate-400 text-sm mt-1">{user.email}</p></div>

        {showChangePw && <ChangePassword />}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-[#0C1017] border border-white/10 mb-6">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider">Overview</TabsTrigger>
            <TabsTrigger value="share" data-testid="admin-tab-share" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider gap-1.5"><Share2 className="h-3.5 w-3.5" /> Share Trade</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="data-[state=active]:bg-brand data-[state=active]:text-white text-slate-400 font-data text-xs uppercase tracking-wider gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Stat testId="admin-stat-users" icon={Users} label="Users" value={stats?.users ?? "—"} />
              <Stat testId="admin-stat-paying" icon={DollarSign} label="Paying Members" value={stats?.paying_members ?? "—"} />
              <Stat testId="admin-stat-posts" icon={FileText} label="Community Posts" value={stats?.posts ?? "—"} />
              <Stat testId="admin-stat-journal" icon={BookOpen} label="Journal Entries" value={stats?.journal_entries ?? "—"} />
              <Stat testId="admin-stat-analyses" icon={Cpu} label="AI Analyses" value={stats?.analyses ?? "—"} />
              <Stat testId="admin-stat-messages" icon={MessageSquare} label="Enquiries" value={stats?.contact_messages ?? "—"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card-surface rounded-xl p-5">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-brand" /> Contact Enquiries</h3>
                <div data-testid="admin-contact-list" className="space-y-3 max-h-96 overflow-y-auto">
                  {messages.length === 0 ? <p className="text-sm text-slate-500">No enquiries yet.</p> : messages.map((m) => (
                    <div key={m.id} className="rounded-lg bg-[#0C1017] border border-white/8 p-3">
                      <div className="flex justify-between items-center"><span className="text-sm font-semibold text-white">{m.name}</span><span className="font-data text-[10px] text-slate-500">{(m.created_at || "").slice(0, 10)}</span></div>
                      <div className="font-data text-[11px] text-brand">{m.email} · {m.subject}</div>
                      <p className="text-sm text-slate-300 mt-1">{m.message}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-surface rounded-xl p-5">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-brand" /> Members</h3>
                <div data-testid="admin-users-list" className="space-y-2 max-h-96 overflow-y-auto">
                  {users.map((u) => (
                    <MemberRow key={u.user_id} u={u} onGranted={loadAll} />
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="share">
            <div className="flex items-center gap-2 mb-5">
              <Share2 className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Share Trade with Community</h2>
            </div>
            <AdminShareTrade />
          </TabsContent>

          <TabsContent value="analytics">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 className="h-5 w-5 text-brand" />
              <h2 className="font-heading text-xl font-bold">Website Analytics</h2>
            </div>
            <AdminAnalytics />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
